import { atom, useAtom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { feedbackService } from '@/services/feedback.service';
import { queryClient } from '@/lib/query-client';
import type { FeedbacksFilters } from '@/types';

export const feedbacksFiltersAtom = atom<FeedbacksFilters>({});

export const feedbacksPaginationAtom = atom<{ page: number; pageSize: number }>({
  page: 1,
  pageSize: 20
});

const feedbacksQueryAtom = atomWithQuery((get) => {
  const filters = get(feedbacksFiltersAtom);
  const pagination = get(feedbacksPaginationAtom);
  return {
    queryKey: ['feedbacks', filters, pagination],
    queryFn: () => feedbackService.list({ ...filters, ...pagination }),
    staleTime: 30_000
  };
});

const feedbackReasonsQueryAtom = atomWithQuery(() => ({
  queryKey: ['feedback-reasons'],
  queryFn: () => feedbackService.listReasons(),
  staleTime: Infinity
}));

export const useFeedbacks = () => {
  const [result] = useAtom(feedbacksQueryAtom);
  return result;
};

export const useFeedbackReasons = () => {
  const [result] = useAtom(feedbackReasonsQueryAtom);
  return result.data ?? [];
};

export const useFeedbacksFilters = () => useAtom(feedbacksFiltersAtom);
export const useFeedbacksPagination = () => useAtom(feedbacksPaginationAtom);

const refresh = () =>
  queryClient.invalidateQueries({ queryKey: ['feedbacks'] });

export const useFeedbackMutations = () => ({
  markAddressed: async (id: string, note: string) => {
    await feedbackService.markAddressed(id, note);
    await refresh();
  },
  unmarkAddressed: async (id: string) => {
    await feedbackService.unmarkAddressed(id);
    await refresh();
  }
});
